/**
 * Custom tRPC link that automatically injects the active workspaceId
 * from the WorkspaceContext into all API requests.
 *
 * This ensures the backend always receives the correct workspace context
 * without requiring manual workspaceId passing in every API call.
 */
import { TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { AnyRouter } from '@trpc/server';

// Global workspace ID that will be set by the WorkspaceProvider
let globalWorkspaceId: string | null = null;

/**
 * Set the global workspace ID
 * This should be called by the WorkspaceProvider whenever the workspace changes
 */
export function setGlobalWorkspaceId(workspaceId: string | null) {
  globalWorkspaceId = workspaceId;
}

/**
 * Get the current global workspace ID
 */
export function getGlobalWorkspaceId(): string | null {
  return globalWorkspaceId;
}

/**
 * Custom tRPC link that injects workspaceId into all requests
 */
export function workspaceLink<TRouter extends AnyRouter>(): TRPCLink<TRouter> {
  return () => {
    return ({ next, op }) => {
      return observable((observer) => {
        // Get the current workspace ID
        const workspaceId = getGlobalWorkspaceId();

        // If we have a workspaceId and the input is an object, inject it
        if (workspaceId && typeof op.input === 'object' && op.input !== null) {
          // Only inject if workspaceId is not already present in the input
          if (!('workspaceId' in op.input)) {
            op.input = {
              ...op.input,
              workspaceId,
            };
          }
        } else if (workspaceId && (op.input === undefined || op.input === null)) {
          // If there's no input at all, create one with just workspaceId
          op.input = { workspaceId };
        }

        // Continue with the modified operation
        const unsubscribe = next(op).subscribe({
          next(value) {
            observer.next(value);
          },
          error(err) {
            observer.error(err);
          },
          complete() {
            observer.complete();
          },
        });

        return unsubscribe;
      });
    };
  };
}
